<template>
  <v-container fluid>
    <v-btn
      :to="{ name: 'admin.index' }"
      elevation="0"
      tile
      icon
    >
      <v-icon size="20">mdi-arrow-left</v-icon>
    </v-btn>

    <v-divider class="mb-3 mt-1"></v-divider>

    <v-row>
      <v-col cols="3">
        <v-form>
          <v-row>
            <v-col cols="12">
              <v-text-field
                prepend-icon="mdi-account-circle"
                v-model="form.email"
                @input="search"
                label="Email"
                v-if="!toggle"
                hide-details
                solo
                dense
              ></v-text-field>

              <v-select
                :items="groups"
                @change="selectGroup"
                menu-props="auto"
                label="Select a group"
                hide-details
                clearable
                prepend-icon="mdi-account-supervisor-circle"
                single-line
                solo
                dense
                v-else
              ></v-select>

              <v-switch
                class="ml-2 mr-2"
                v-model="toggle"
                @change="clearInputs"
                label="Email/Group"
                hide-details
              ></v-switch>

              <div class="text-right mt-2">
                <v-btn
                  @click.prevent="submit"
                  :disabled="disabled"
                  type="submit"
                  color="primary"
                  small
                >Save</v-btn>
              </div>
            </v-col>

            <v-col cols="12">
              <v-alert
                :value="showAlert"
                color="#2A3B4D"
                icon="mdi-firework"
                dark
                dense
              >
                Permissions updated
              </v-alert>
            </v-col>
          </v-row>
        </v-form>
      </v-col>

      <v-col cols="9">
        <v-row>
          <v-col cols="4" v-for="g in grouped" :key="g.guid">
            <h3>
              <span class="d-block text-wrap">{{ g.name }}</span>
              <v-checkbox
                :value="allSelected[g.guid]"
                @change="selectAll($event, g)"
                label="Mark all"
              ></v-checkbox>
            </h3>

            <ul>
              <li>
                <v-checkbox
                  v-model="form.rights"
                  :label="g.name"
                  :value="g.guid"
                  :disabled="!form.email &&
                    !selectedGroup.name ? true : false"
                  @change="allAreSelected"
                  hide-details
                  dense
                ></v-checkbox>
              </li>

              <li v-for="c in g.childs" :key="c.guid">
                <v-checkbox
                  v-model="form.rights"
                  :label="c.name"
                  :value="c.guid"
                  :disabled="!form.email &&
                    !selectedGroup.name ? true : false"
                  @change="allAreSelected"
                  hide-details
                  dense
                ></v-checkbox>
              </li>
            </ul>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Menu, Permission, Groups } from '@/api';

export default {
  name: 'app-permission-create',
  data() {
    return {
      showAlert: false,
      toggle: false,
      selectedGroup: {},
      availableGroups: [],
      disabled: false,
      menus: [],
      allSelected: {},
      form: {
        email: '',
        rights: [],
      },
    };
  },
  computed: {
    groups() {
      return this.availableGroups
        .map(item => ({
          text: item.name,
          value: item.guid,
        }))
        .sort((a, b) => (
          a.text > b.text ? 1 : -1
        ));
    },
    grouped() {
      return this.menus.reduce((h, c) => {
        if (c.parent === null && !h.some(i => i.guid === c.guid)) {
          h.push({
            guid: c.guid,
            name: c.titleEN || c.titleFR,
            childs: [],
          });
        } else {
          const parentGUID = this.getParentGuid(c);
          let parent = h.find(i => i.guid === parentGUID);

          // There's a problem when the parent is created after the childs, it will throw an error:
          // cannot read guid of undefined. This is a hotfix to create the parent if it doesn't
          // exists yet.
          if (parent === undefined) {
            const menuParent = this.menus.find(i => i.guid === parentGUID);
            const newParent = {
              guid: menuParent.guid,
              name: menuParent.titleEN || menuParent.titleFR,
              childs: [],
            };

            h.push(newParent);
            parent = newParent;
          }

          // Hotfix "parent !== undefined", no idea why the parent could be undefined, project
          // is too old and haven't worked on it for a long time. A refactor would be nice if
          // the project is still alive in the future.
          if (parent !== undefined) {
            parent.childs.push({
              guid: c.guid,
              name: this.getPath(c, []).reverse().join('.'),
            });
          }
        }

        return h;
      }, []);
    },
  },
  methods: {
    clearInputs() {
      this.form.email = '';
      this.selectedGroup = {};
      this.allSelected = {};
      this.form.rights = [];
    },
    selectGroup(group) {
      if (group) {
        this.selectedGroup = this.availableGroups.find(item => item.guid === group);
        this.form.rights = this.selectedGroup.permissions.map(item => item.menuId);
        this.allAreSelected(this.selectedGroup.permissions);
      } else {
        this.clearInputs();
      }
    },
    selectAll(check, group) {
      if (check) {
        this.form.rights = window._.union(
          this.form.rights,
          window._.concat(
            [group.guid],
            group.childs.map(g => g.guid),
          ),
        );
      } else {
        this.form.rights = window._.difference(
          this.form.rights,
          window._.concat(
            [group.guid],
            group.childs.map(c => c.guid),
          ),
        );
      }
    },
    async submit() {
      this.disabled = true;
      if (Object.entries(this.selectedGroup).length === 0) {
        try {
          await Permission.create(this.form);
          this.showAlert = true;
          setTimeout(() => { this.showAlert = false; }, 2000);
        } catch (e) {
          console.debug('ERROR', e);
        }
      } else {
        try {
          await Groups.permissions(this.selectedGroup.guid, this.form.rights);
          this.showAlert = true;
          setTimeout(() => { this.showAlert = false; }, 2000);
          this.fetchGroups();
        } catch (e) {
          console.debug('ERROR', e);
        }
      }

      this.disabled = false;
    },
    allAreSelected() {
      this.grouped.map((g) => {
        const parent = this.form.rights.includes(g.guid);
        const childs = g.childs.every(c => this.form.rights.includes(c.guid));
        this.allSelected[g.guid] = parent && childs;
        return g;
      });
    },
    search: (function debounce() {
      let time = 0;

      return function search() {
        const self = this;

        clearTimeout(time);
        time = setTimeout(async () => {
          self.form.rights = [];

          if (self.form.email) {
            (await Permission.fetch({ email: self.form.email }))
              .data
              .map((p) => {
                self.form.rights.push(p.menuId);
                self.allAreSelected();
                return p;
              });
          }
        }, 500);
      };
    }()),
    getParentGuid(item) {
      if (item.parent) return this.getParentGuid(item.parent);
      return item.guid;
    },
    getPath(item, path) {
      if (item.parent) {
        path.push(item.titleEN || item.titleFR);
        return this.getPath(item.parent, path);
      }

      path.push(item.titleEN || item.titleFR);

      return path;
    },
    async fetch() {
      this.menus = (await Menu.fetch()).data;
    },
    async fetchGroups() {
      this.$store.commit('START_LOADING');

      try {
        this.availableGroups = (await Groups.fetch({ permissions: true })).data;
      } catch (e) {
        console.debug('ERROR', e);
      }

      this.$store.commit('END_LOADING');
    },
  },
  mounted() {
    this.fetch();
    this.fetchGroups();
  },
};
</script>
